// Configuration for each window type defining which tabs to show and special behaviors
// This keeps all business logic in one place for easy maintenance

const WINDOW_TYPE_CONFIGS = {
  Architectural: {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "ArchitecturalDiagram",
  },

  Awning: {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "AwningDiagram",
  },

  "Basement 2-Part Slider": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "BasementSliderDiagram",
  },

  Bay: {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: false,
    diagram: "BayDiagram",
  },

  Bow: {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: false,
    diagram: "BowDiagram",
  },

  "Single Casement": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "SingleCasementDiagram",
  },

  "2-Lite Casement": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "TwoLiteCasementDiagram",
  },

  "3-Lite Casement": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "ThreeLiteCasementDiagram",
  },

  "4-Lite Casement": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "FourLiteCasementDiagram",
  },

  "Double Hung": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "DoubleHungDiagram",
  },

  Garden: {
    tabs: ["measurements", "job_photos"],
    hasRemoval: false,
    diagram: "GardenDiagram",
  },

  Picture: {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "PictureDiagram",
  },

  "2 Part Slider": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    hasRemoval: true,
    diagram: "TwoPartSliderDiagram",
  },

  "3 Part Slider": {
    tabs: ["measurements", "grid", "obscurity", "job_photos"],
    sashBased: {
      gridLocation: true,
      obscurity: true,
    },
    hasRemoval: true,
    diagram: "ThreePartSliderDiagram",
  },

  "2-Lite Sliding Glass Door": {
    tabs: ["sizes", "grid", "obscurity", "blinds", "job_photos"],
    hasRemoval: false,
    diagram: "TwoLiteSlidingDoorDiagram",
    // Define when tabs should be hidden based on form state
    tabVisibilityRules: {
      blinds: {
        hideWhen: (formData, availableOptions) => {
          const selectedSize = availableOptions.window_product_sizes.find(
            (size) => size.id === formData.window_product_size_id
          );
          return selectedSize?.name === "Custom";
        },
      },
      grid: {
        hideWhen: (formData) => {
          return !!formData.internal_blind_id;
        },
      },
    },
    // Define which fields should be cleared when other fields change
    fieldClearingRules: {
      window_product_size_id: (value, formData, availableOptions) => {
        const selectedSize = availableOptions.window_product_sizes.find(
          (size) => size.id === value
        );
        // Clear internal blind when Custom size is selected
        if (selectedSize?.name === "Custom") {
          return ["internal_blind_id"];
        }
        return [];
      },
      internal_blind_id: (value) => {
        // Clear grid fields when internal blinds are selected
        if (value) {
          return ["grid_location_id", "grid_style_id"];
        }
        return [];
      },
    },
  },

  "3-Lite Sliding Glass Door": {
    tabs: ["sizes", "grid", "obscurity", "job_photos"],
    hasRemoval: false,
    diagram: "ThreeLiteSlidingDoorDiagram",
  },
};

export const getWindowConfig = (windowTypeName) => {
  const config = WINDOW_TYPE_CONFIGS[windowTypeName];

  if (!config) {
    console.warn(`No configuration found for window type: ${windowTypeName}`);
    return {
      tabs: ["measurements"],
    };
  }

  return config;
};

export const getVisibleTabs = (windowTypeName, formData, availableOptions) => {
  const config = getWindowConfig(windowTypeName);

  return config.tabs.filter((tab) => {
    const rule = config.tabVisibilityRules?.[tab];
    if (!rule) return true;

    // Tab is visible if hideWhen returns false
    return !rule.hideWhen(formData, availableOptions);
  });
};

export const getFieldsToClear = (
  windowTypeName,
  field,
  value,
  formData,
  availableOptions
) => {
  const config = getWindowConfig(windowTypeName);
  const rule = config.fieldClearingRules?.[field];

  if (!rule) return [];
  return rule(value, formData, availableOptions);
};

// Map of which fields belong to which tabs
const TAB_FIELDS = {
  measurements: ["width", "height"],
  sizes: ["window_product_size_id", "width", "height"],
  grid: [
    "grid_location_id",
    "grid_style_id",
    "end_lite_grid_location_id",
    "end_lite_style_id",
  ],
  obscurity: ["obscure_glass_option_id"],
  blinds: ["internal_blind_id"],
  job_photos: ["job_photos", "job_photos_to_remove", "existing_job_photos"],
};

// Fields that are always visible (not tied to tabs)
const ALWAYS_VISIBLE_FIELDS = [
  "architectural_style",
  "color",
  "window_removal_id",
  "panel_ratio_id",
];

export const shouldRenderHiddenField = (fieldName, visibleTabs) => {
  // Always render fields that aren't tied to tabs
  if (ALWAYS_VISIBLE_FIELDS.includes(fieldName)) {
    return true;
  }

  // Check if field belongs to any visible tab
  return visibleTabs.some((tab) => {
    const tabFields = TAB_FIELDS[tab] || [];
    return tabFields.includes(fieldName);
  });
};

export default WINDOW_TYPE_CONFIGS;
